import { https } from "../http";
import store from "../store";

import {
  setCreateFolderLoading,
  setFolderLoading,
  setFolders,
  setFileLoading,
  setFiles,
  setUploadFileLoading,
  setRenameFileLoading,
  setUpdateFileLoading,
  setExpiredFiles,
} from "../store/reducers/document";
import { showError, showInfo } from "../utils/index";

const getFolders = async (userId, userType, search) => {
  try {
    store.dispatch(setFolderLoading(true));

    let queryStr = `/document/folder/get?${userType}=${userId}`

    if (search) {
      queryStr = queryStr + `&search=${search}`;
    }

    const response = await https().get(
      queryStr
    );

    if (response) {
      store.dispatch(setFolders(response.data));
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    return false;
  } finally {
    store.dispatch(setFolderLoading(false));
  }
};

const getFiles = async (userId, userType, folderId, search) => {
  try {
    store.dispatch(setFileLoading(true));

    let url = `/document/file/get?${userType}=${userId}`;
    if (folderId) url += `&folder=${folderId}`;
    if (search) url += `&search=${search}`;

    const response = await https().get(url);

    if (response) {
      store.dispatch(setFiles(response.data));
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
  } finally {
    store.dispatch(setFileLoading(false));
  }
};

const createFolder = async (data, userId, userType) => {
  try {
    store.dispatch(setCreateFolderLoading(true));
    let url = `/document/folder/create?${userType}=${userId}`;
    const response = await https().post(url, data);

    if (response) {
      store.dispatch(setCreateFolderLoading(false));
      getFolders(userId, userType);
      // showInfo(response.data.message);
      return true;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    store.dispatch(setCreateFolderLoading(false));
    return false;
  }
};

const uploadFiles = async (payload, folderId, userId, userType) => {
  try {
    store.dispatch(setUploadFileLoading(true));

    let url = `/document/file/create?${userType}=${userId}`;
    if (folderId) url += `&folder=${folderId}`;

    const response = await https().post(url, payload);

    if (response) {
      await getFiles(userId, userType, folderId);
      return true;
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    return false;
  } finally {
    store.dispatch(setUploadFileLoading(false));
  }
};

const updateFiles = async (
  data,
  fileId,
  userId,
  userType,
  folderId,
  deleted
) => {
  try {
    store.dispatch(setUploadFileLoading(true));
    let url = `/document/file/update?${userType}=${userId}&fileId=${fileId}`;
    if (folderId) url += `&folder=${folderId}`;

    let payload;
    if (deleted) {
      payload = { deleted: true };
    } else {
      payload = data;
    }

    const response = await https().put(url, payload);
    if (response) {
      return true;
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    store.dispatch(setUploadFileLoading(false));
    return false;
  } finally {
    store.dispatch(setUploadFileLoading(false));
  }
};

const fileStatus = async (fileId, userId, userType, deleted) => {
  try {
    store.dispatch(setUpdateFileLoading(true));

    // Build URL
    let url = `/document/file/update-status?${userType}=${userId}&fileId=${fileId}`;
    let payload = { deleted };

    // Send PUT request
    const response = await https().put(url, payload);

    if (response) {
      
      // Refresh file list
      return true;
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    store.dispatch(setUpdateFileLoading(false));
    return false;
  } finally {
    store.dispatch(setUpdateFileLoading(false));
  }
};

const updateFolder = async (data, userId, userType) => {
  try {
    store.dispatch(setCreateFolderLoading(true));
    let response = await https().post(`/document/folder/update`, data);
    if (response) {
      store.dispatch(setCreateFolderLoading(false));
      getFolders(userId, userType);
      // showInfo(response.data.message);
      return true;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    store.dispatch(setCreateFolderLoading(false));
    return false;
  }
};

const deleteFolder = async (folderId, userId, userType) => {
  try {
    store.dispatch(setCreateFolderLoading(true));
    let response = await https().delete(
      `/document/folder/delete?folder=${folderId}`
    );
    if (response) {
      store.dispatch(setCreateFolderLoading(false));
      getFolders(userId, userType);
      return true;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    store.dispatch(setCreateFolderLoading(false));
    return false;
  }
};

const renameFile = async (data, folderId, userId, userType) => {
  try {
    store.dispatch(setRenameFileLoading(true));

    const response = await https().post(`/document/file/rename`, data);

    if (response) {
      // the rename/move itself is done here — the listings are re-read in the
      // background so the caller can close and confirm right away instead of
      // waiting on two more round trips
      const isMove = Object.prototype.hasOwnProperty.call(data || {}, "folder");
      // refresh the folder currently being viewed (folderId), NOT the file's new/destination
      // folder, so the moved file disappears from the current view and no destination data leaks in
      getFiles(userId, userType, folderId).then(() => {
        // a move also changes which folder owns the file, so the folder listing
        // (and the files it carries for the zip download) has to be re-read
        if (isMove) getFolders(userId, userType);
      });
      return true;
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    return false;
  } finally {
    store.dispatch(setRenameFileLoading(false));
  }
};

const deleteFile = async (fileId, folder, userId, userType) => {
  try {
    store.dispatch(setRenameFileLoading(true));
    let response = await https().delete(`/document/file/delete?file=${fileId}`);
    if (response) {
      store.dispatch(setRenameFileLoading(false));
      await getFiles(userId, userType, folder);
      return true;
    }
  } catch (error) {
    let _error = error?.response?.data?.message;

    // showError(_error, error?.response?.status);
    store.dispatch(setRenameFileLoading(false));
    return false;
  }
};


const getExpiredFiles=async()=>{

    try {
    store.dispatch(setFileLoading(true));

    let url = '/document/file/expired'
 
    const response = await https().get(url);

    if (response) {
      store.dispatch(setExpiredFiles(response.data));
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
  } finally {
    store.dispatch(setFileLoading(false));
  }

}

const uploadToTemplate = async (fileId, data, userId, userType, folderId) => {
  try {
    store.dispatch(setUploadFileLoading(true));
    const response = await https().put(
      `/document/file/upload-to-template?fileId=${fileId}`,
      data
    );
    if (response?.status === 200) {
      await getFiles(userId, userType, folderId);
      return true;
    }
  } catch (error) {
    const _error = error?.response?.data?.message;
    showError(_error, error?.response?.status);
    return false;
  } finally {
    store.dispatch(setUploadFileLoading(false));
  }
};

export {
  createFolder,
  getFolders,
  updateFolder,
  uploadFiles,
  fileStatus,
  updateFiles,
  getFiles,
  deleteFolder,
  deleteFile,
  renameFile,
  getExpiredFiles,
  uploadToTemplate,
};
